/**
 * HALO - Invite Employees
 * Page for adding new team members
 */

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Mail,
  Building2,
  Check,
  AlertCircle,
  ArrowLeft,
  Copy,
  ChevronDown,
} from "lucide-react";
import {
  createEmployeeAccount,
  getOrganizationDepartments,
} from "../lib/supabase";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function InviteEmployees() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [department, setDepartment] = useState("");
  const [departments, setDepartments] = useState<string[]>([]);
  const [role, setRole] = useState<"employee" | "manager">("employee");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  // Load departments
  useEffect(() => {
    async function loadDepts() {
      const depts = await getOrganizationDepartments();
      setDepartments(depts);
    }
    loadDepts();
  }, []);

  const generatePassword = () => {
    const chars =
      "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%";
    let pass = "";
    for (let i = 0; i < 12; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pass;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Auto-generate password for invitation flow
    const password = generatePassword();

    const result = await createEmployeeAccount(
      email,
      password,
      fullName,
      department,
      role,
    );

    setLoading(false);

    if (result.success) {
      setSuccessData({ email, password });
      // In a real app, this would trigger an email.
      // For now, we show the credentials.
    } else {
      setError(result.error || "Failed to create employee account");
    }
  };

  const copyCredentials = () => {
    if (successData) {
      const text = `Welcome to HALO!\n\nHere are your login credentials:\nEmail: ${successData.email}\nPassword: ${successData.password}\n\nPlease log in and change your password.`;
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleReset = () => {
    setSuccessData(null);
    setEmail("");
    setFullName("");
    setDepartment("");
    setRole("employee");
    setError("");
  };

  if (successData) {
    return (
      <div className="max-w-xl mx-auto py-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="shadow-2xl border-signal-green/30">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-signal-green/10 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-signal-green" />
              </div>
              <CardTitle className="text-2xl font-semibold text-foreground">
                Employee Invited!
              </CardTitle>
              <CardDescription>Account created successfully.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-4">
              <div className="bg-surface-elevated rounded-xl p-6 border border-glass-grey">
                <h3 className="text-sm font-medium text-muted-slate mb-4 uppercase tracking-wider">
                  Login Credentials
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-muted-slate">Email</label>
                    <p className="text-foreground font-mono text-base break-all">
                      {successData.email}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-slate">
                      Temporary Password
                    </label>
                    <p className="text-foreground font-mono text-base bg-surface-dark px-3 py-2 rounded-lg mt-1 select-all">
                      {successData.password}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <motion.button
                  onClick={copyCredentials}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-surface-elevated hover:bg-glass-grey text-foreground rounded-xl transition-colors border border-glass-grey font-medium"
                  whileTap={{ scale: 0.98 }}
                >
                  <Copy className="w-4 h-4" />
                  {copied ? "Copied to Clipboard" : "Copy Invitation Details"}
                </motion.button>
                <div className="flex gap-3">
                  <motion.button
                    onClick={() => navigate("/admin")}
                    className="flex-1 px-4 py-3 bg-surface-dark hover:bg-surface-elevated text-foreground rounded-xl transition-colors border border-glass-grey"
                    whileTap={{ scale: 0.98 }}
                  >
                    Back to Dashboard
                  </motion.button>
                  <motion.button
                    onClick={handleReset}
                    className="flex-1 px-4 py-3 bg-primary hover:opacity-90 text-white rounded-xl font-medium transition-opacity"
                    whileTap={{ scale: 0.98 }}
                  >
                    Invite Another
                  </motion.button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-slate hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <h1 className="text-2xl font-bold text-foreground">
          Invite New Employee
        </h1>
        <p className="text-muted-slate">
          Add a new team member to your organization.
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6 pt-6">
            {error && (
              <div className="p-4 rounded-xl bg-signal-crimson/10 border border-signal-crimson/20 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-signal-crimson flex-shrink-0 mt-0.5" />
                <p className="text-sm text-signal-crimson">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-muted-slate mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-slate" />
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    className="halo-input pl-11"
                    placeholder="e.g. Jane Smith"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-muted-slate mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-slate" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="halo-input pl-11"
                    placeholder="e.g. jane@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Department */}
                <div>
                  <label className="block text-sm font-medium text-muted-slate mb-2">
                    Department
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-slate z-10" />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="halo-input pl-11 flex items-center justify-between w-full text-left"
                        >
                          <span
                            className={
                              department
                                ? "text-foreground"
                                : "text-muted-slate"
                            }
                          >
                            {department || "Select Department"}
                          </span>
                          <ChevronDown className="w-4 h-4 text-muted-slate" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="w-[240px] max-h-[200px] overflow-y-auto"
                      >
                        {departments.map((dept) => (
                          <DropdownMenuItem
                            key={dept}
                            onClick={() => setDepartment(dept)}
                          >
                            {dept}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                          <input
                            type="text"
                            placeholder="Type new department..."
                            className="w-full bg-transparent border-none focus:outline-none text-sm p-1"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                const val = (e.target as HTMLInputElement)
                                  .value;
                                if (val) {
                                  setDepartment(val);
                                  // Close menu logic would happen naturally or use controlled state
                                }
                              }
                            }}
                            onChange={(e) => setDepartment(e.target.value)}
                            value={
                              departments.includes(department) ? "" : department
                            }
                          />
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-muted-slate mb-2">
                    Role
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setRole("employee")}
                      className={`flex-1 py-2.5 px-3 rounded-xl border transition-all text-sm font-medium ${
                        role === "employee"
                          ? "bg-primary/10 border-primary text-primary"
                          : "bg-surface-dark border-glass-grey text-muted-slate hover:border-muted-slate"
                      }`}
                    >
                      Employee
                    </button>
                    <button
                      type="button"
                      onClick={() => setRole("manager")}
                      className={`flex-1 py-2.5 px-3 rounded-xl border transition-all text-sm font-medium ${
                        role === "manager"
                          ? "bg-signal-blue/10 border-signal-blue text-signal-blue"
                          : "bg-surface-dark border-glass-grey text-muted-slate hover:border-muted-slate"
                      }`}
                    >
                      Manager
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="pt-2 pb-6 flex justify-end gap-3">
            <motion.button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-surface-dark hover:bg-surface-elevated text-foreground rounded-xl transition-colors border border-glass-grey"
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </motion.button>
            <motion.button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-primary hover:opacity-90 text-white rounded-xl font-medium transition-opacity flex items-center gap-2"
              whileTap={{ scale: 0.98 }}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-[var(--foreground)]/30 border-t-[var(--foreground)] rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  Send Invite
                </>
              )}
            </motion.button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
